import { captureEvent } from '@sentry/nextjs';
import { getTimezoneOffset as getTimezoneOffsetFns } from 'date-fns-tz';
import { saveAs } from 'file-saver';

import { fetchData, postData } from '@framework/api/apiCalls';
import { api } from '@framework/constants/endpoints';
import { isNilOrEmpty } from '@framework/cookbook';
import { camelizeKeys } from '@framework/utils/camelize.utils';

/* Regular Expression taken from Chromium:
     https://cs.chromium.org/chromium/src/third_party/WebKit/LayoutTests/fast/forms/resources/ValidityState-typeMismatch-email.js?sq=package:chromium&type=cs */
export const VALID_EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isWindowDefined = () => typeof window !== 'undefined';

export const safeNumber = (value) => (Number.isNaN(Number(value)) ? 0 : Number(value));

export const changeLocation = (path) => {
    if (isWindowDefined()) window.location.assign(path);
};

export const isValidEmail = (value) => {
    return VALID_EMAIL_REGEX.test(value);
};

/**
 * @param {{ url: string, name: string, mimeType?: string | null, password?: string, onError?: function | null, onDownload?: function | null }} options
 */
export const saveFile = ({
    url,
    name,
    mimeType = null,
    password = '',
    onError = null,
    onDownload = null
}) => {
    if (!isWindowDefined()) {
        return null;
    }
    if (onDownload) {
        onDownload();
    }
    const requestHeaders = { ...(password ? { 'x-pdf-password': password } : {}) };
    return fetchData(url, { responseType: 'blob', headers: requestHeaders })
        .then((e) => {
            const { headers } = camelizeKeys(e);
            const nameFromHeader =
                headers?.contentDisposition?.split && headers.contentDisposition.split('"')[1];
            return saveAs(
                e.data,
                (name ?? nameFromHeader).replace(/\./g, '-'),
                mimeType ?? headers.contentType
            );
        })
        .catch((error) => {
            onError?.(error);
            captureEvent({
                message: 'Failed to save file',
                level: 'info',
                extra: {
                    status: error?.status,
                    response: error?.data
                }
            });
        });
};

// String:URL -> Object:hashQuery
// sherpany.com/path/#id/33/#sort/ascending -> {id: 33, sort: 'ascending'}
export const getURLHashFragments = (url) =>
    url
        .split('#')
        .slice(1)
        .reduce((acc, current) => {
            const hashValue = current.split('/');
            return { ...acc, [hashValue[0]]: hashValue[1] };
        }, {});

export const redirectToURL = (url) => {
    window.location = `/${url}/`;
};

export const generateSlug = (text) => {
    return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w-]+/g, '') // Remove all non-word chars
        .replace(/--+/g, '-'); // Replace multiple - with single -
};

/**
 * Format date with Intl.DateTimeFormat, default format: DD.MM.YYYY HH24:MM
 * @param {date|number} date
 * @param {object} options - Intl.DateTimeFormat options.
 */
export const getTimeZoneDate = (date, options = {}) => {
    return new Intl.DateTimeFormat('de-CH', {
        ...{
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour12: false,
            hour: 'numeric',
            minute: 'numeric',
            timeZone: 'Europe/Zurich'
        },
        ...options
    })
        .format(date)
        .replace(',', '');
};

/**
 * Returns timezone offset +/-HH:MM
 * @param {string} timezone
 * @param {date} date
 */
export const getTimeZoneOffset = (timeZone, date) => {
    const format = (number) => `0${Math.abs(number)}`.slice(-2);

    const offset = getTimezoneOffsetFns(timeZone, date) / 1000 / 60 / 60;
    const hoursOffset = Math.trunc(offset);
    const minutesOffset = Math.trunc(hoursOffset === 0 ? offset * 60 : (hoursOffset - offset) * 60);
    return `${hoursOffset < 0 || (hoursOffset === 0 && minutesOffset < 0) ? '-' : '+'}${format(
        hoursOffset
    )}:${format(minutesOffset)}`;
};

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * `wait` milliseconds.
 * @param {function} fn
 * @param {number} wait
 */
export const debounce = (fn, wait) => {
    let timeout;

    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            fn(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

export const toDegrees = (angle) => angle * (180 / Math.PI);
export const toRadians = (angle) => angle * (Math.PI / 180);
export const getAngle = (base, hyp) => Math.acos(base / hyp);

/**
 * Convert size in bytes to human readable format
 * @param {number} bytes
 */
export const bytesToSize = (bytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
    return `${Math.round(bytes / 1024 ** i, 2)} ${sizes[i]}`;
};

export const logout = () => {
    postData(api.logout()).then(({ data }) => {
        window.location = data.redirectUrl;
    });
};

export const alphanumericalSort = (arr, prop, options = {}) => {
    return arr.sort((a, b) =>
        (prop ? a[prop] : a).localeCompare(prop ? b[prop] : b, undefined, {
            numeric: true,
            sensitivity: 'base',
            ...options
        })
    );
};

export const retry = (fn, conditionalFn, onSuccess) => {
    const _retry = () => {
        setTimeout(async () => {
            const response = await fn();

            if (conditionalFn(response)) {
                onSuccess(response);
            } else {
                _retry();
            }
        }, 500);
    };
    _retry();
};

export const timeFromMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const minutesLeft = minutes % 60;

    if (hours && minutesLeft) {
        return `${hours}h ${minutesLeft}m`;
    }

    if (hours) {
        return `${hours}h`;
    }

    return `${minutes}m`;
};

export const splitNotificationErrorMessage = (errorMessage) => {
    if (isNilOrEmpty(errorMessage)) {
        return { message: '' };
    }
    if (errorMessage.indexOf('. ') === -1) {
        return { message: errorMessage };
    }
    const [header, message] = errorMessage.split('. ');
    return { header, message };
};

/**
 * Load more entries on scroll, used mainly on user selectors
 * When the scroll reaches the end of the container,
 * if there are more entries to fetch, fetch them
 * @param {SyntheticEvent<HTMLDivElement>} event
 * @param {boolean} isLoading
 * @param {boolean} isFetchingNextPage
 * @param {boolean} hasNextPage
 * @param {function} fetchNextPage
 */
export const fetchNextPageOnScroll = ({
    event,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
}) => {
    const target = event?.currentTarget;

    if (
        Math.ceil(safeNumber(target?.scrollTop) + safeNumber(target?.offsetHeight)) >=
        safeNumber(target?.scrollHeight) - 100
    ) {
        if (!isLoading && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }
};
